import React, { useEffect, useMemo, useState } from "react";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_TableOptions,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
  useMaterialReactTable,
} from "material-react-table";

import {
  Box,
  Button,
  IconButton,
  Modal,
  Tooltip,
  Typography,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import {
  useGetRules,
  useUpdateRule,
  useDeleteRule,
  useCreateRule,
  useApplyRules,
  useExportRules,
  useDeleteRules,
} from "../../../hooks/rules";
import { Rule } from "../../../types/rule";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import ImportFromCSV from "../../csvimport/importFromCsv";
import { baseURL } from "../../../api/apiClient";

const RulesTable = () => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [globalFilter, setGlobalFilter] = useState<string | undefined>("");
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  useEffect(() => {
    setPagination({
      pageIndex: 0,
      pageSize: 10,
    });
  }, [columnFilters]);

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const openImportModal = () => setIsImportModalOpen(true);
  const closeImportModal = () => setIsImportModalOpen(false);

  const deleteRules = useDeleteRules(columnFilters, sorting, rowCount);

  const triggerDelete = async () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete all currently filtered Rules?"
    );
    if (confirmDelete) {
      try {
        await deleteRules();
      } catch (error) {
        console.error(error);
      }
    }
  };

  const { mutateAsync: deleteRule } = useDeleteRule();

  const {
    data: rulesQueryResults = [],
    isError: isLoadingRulesError,
    isFetching: isFetchingRules,
    isLoading: isLoadingRules,
  } = useGetRules({ pagination, columnFilters, sorting, globalFilter });

  const fetchedRules = rulesQueryResults[0]?.rules || [];

  useEffect(() => {
    const count = rulesQueryResults[0]?.rowCount;
    if (count == 0) {
      setRowCount(0);
    }
    if (!count || count === rowCount) {
      return;
    }
    setRowCount(count);
  }, [rulesQueryResults]);

  const handleSaveRule: MRT_TableOptions<Rule>["onEditingRowSave"] = async ({
    values,
    table,
  }) => {
    const newValidationErrors = validateRule(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }
    setValidationErrors({});
    await updateRule(values);
  };

  const handleCreateRule: MRT_TableOptions<Rule>["onCreatingRowSave"] = async ({
    values,
    table,
  }) => {
    const newValidationErrors = validateRule(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }
    setValidationErrors({});
    await createRule(values);
  };

  const openDeleteConfirmModal = (row: MRT_Row<Rule>) => {
    if (window.confirm("Are you sure you want to delete this Risk map?")) {
      deleteRule(row.original.id);
    }
  };

  const exportRules = useExportRules(columnFilters, sorting);

  const handleExport = async () => {
    try {
      await exportRules();
    } catch (error) {
      toast.error("Failed to export");
    } finally {
    }
  };

  function validateRule(rule: Rule) {
    const validateRequired = (value: any) => !!value;

    return {
      name: !validateRequired(rule.title) ? "Name is required" : "",
    };
  }

  const tableName = "rules_table";
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const tableState = JSON.parse(localStorage.getItem("tableState") || "{}");
    return (
      tableState[tableName]?.columnVisibility || {
        id: false,
      }
    );
  });

  useEffect(() => {
    localStorage.setItem(
      "tableState",
      JSON.stringify({
        ...JSON.parse(localStorage.getItem("tableState") || "{}"),
        [tableName]: {
          columnVisibility,
        },
      })
    );
  }, [columnVisibility, tableName]);

  const columns = useMemo<MRT_ColumnDef<Rule>[]>(
    () => [
      {
        accessorKey: "id",
        header: "Id",
        enableEditing: false,
        enableSorting: false,
        size: 80,
      },

      {
        accessorKey: "title",
        header: "Title",
        size: 220,
        enableEditing: (row) => !row.original.id,
        Cell: ({ row }) => {
          return (
            <>
              <Link to={`/riskmap/${row.original.title}`}>
                {row.original.title}
              </Link>
            </>
          );
        },
        muiEditTextFieldProps: {
          type: "text",
          required: true,
          error: !!validationErrors?.name,
          helperText: validationErrors?.name,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              name: undefined,
            }),
        },
      },
      {
        accessorKey: "enabled",
        header: "Enabled",
        size: 100,
        enableSorting: true,
        enableEditing: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "jq",
        header: "Jq",
        size: 240,
        enableSorting: true,
        enableEditing: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 40,
        enableSorting: true,
        enableEditing: true,
        enableColumnFilter: true,
      },

      {
        accessorKey: "priority",
        header: "Priority",
        size: 40,
        enableSorting: true,
        enableEditing: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "notes",
        header: "Notes",
        size: 240,
        enableSorting: true,
        enableEditing: true,
        enableColumnFilter: true,
      },
    ],
    [validationErrors]
  );
  const table = useMaterialReactTable({
    columns,
    enableGlobalFilter: false,
    data: fetchedRules,
    initialState: {
      showColumnFilters: true,
      density: "compact",
      columnVisibility: columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    createDisplayMode: "modal",
    editDisplayMode: "modal",
    selectAllMode: "all",
    enableColumnResizing: true,
    getRowId: (row) => row.id,
    rowNumberDisplayMode: "original",
    muiToolbarAlertBannerProps: isLoadingRulesError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount,
    muiTableContainerProps: {
      sx: {
        minHeight: "500px",
      },
    },
    renderBottomToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "1rem",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="contained"
          onClick={() => {
            table.setCreatingRow(true);
          }}
        >
          New Rule
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            useApplyRules();
          }}
        >
          Apply Rules
        </Button>
        <Button variant="contained" onClick={handleExport}>
          Export {rowCount} Rows
        </Button>
        <Button variant="contained" onClick={openImportModal}>
          Import
        </Button>
        <Modal
          open={isImportModalOpen}
          onClose={closeImportModal}
          id="modal"
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              height: "90vh",
              width: "90vw",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: "8px",
              overflow: "auto",
            }}
          >
            <Typography id="modal-title" variant="h6" component="h2">
              CSV Import
            </Typography>
            <hr />
            <Typography id="modal-description" sx={{ mt: 2 }}></Typography>
            <ImportFromCSV
              apiUrl={`${baseURL}/api/rules/import/`}
              importType="rules"
              onClose={closeImportModal}
            />

            <Button onClick={closeImportModal} sx={{ mt: 2 }}>
              Close
            </Button>
          </Box>
        </Modal>
        <Button variant="contained" color="error" onClick={triggerDelete}>
          Delete {rowCount} Rows
        </Button>
        ;
      </Box>
    ),
    onCreatingRowCancel: () => setValidationErrors({}),
    onEditingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateRule,
    onEditingRowSave: handleSaveRule,

    enableRowActions: true,
    renderRowActionMenuItems: ({ row, table }) => [
      <Box key={row.id}>
        <Tooltip title="Edit">
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>,
    ],
    state: {
      columnFilters,
      globalFilter,
      pagination,
      sorting,
      columnVisibility: columnVisibility,
      isLoading: isLoadingRules,
      showAlertBanner: isLoadingRulesError,
      showProgressBars: isFetchingRules,
    },
  });

  const { mutateAsync: updateRule } = useUpdateRule({ table });
  const { mutateAsync: createRule } = useCreateRule({ table });

  return <MaterialReactTable table={table} />;
};
export default RulesTable;
